/**
 * Quiz Filtering — Storefront API Integration
 *
 * Listens for the quiz:complete event, queries the Shopify Storefront API
 * for products matching OptimealQuizState.globalTags, and stores the
 * normalised results in window.OptimealResults.
 */

const PRODUCTS_PER_PAGE = 250;

function productsQuery(cursor, hatedTags) {
  const afterArg = cursor ? `, after: ${JSON.stringify(cursor)}` : "";
  // Always quote tag values so multi-word tags like "beef liver" are treated as
  // a single term by Shopify's search parser. Without quotes, Shopify splits on
  // whitespace and treats extra words as full-text search terms, which can wipe
  // out all results.
  //
  // The query argument is delimited by outer " in the GraphQL string, so inner
  // double-quotes must be escaped as \" — in this JS template literal that is \\"
  // which produces the literal characters \" in the GraphQL string sent to the API.
  const exclusions = (hatedTags || [])
    .map((t) => ` -tag:\\"${t.replace(/["\\]/g, "")}\\"`)
    .join("");

  return `{
    products(first: ${PRODUCTS_PER_PAGE}, query: "tag:quiz-product${exclusions}"${afterArg}) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          title
          handle
          tags
          description
          metafields(identifiers: [
            { namespace: "custom", key: "tags" },
            { namespace: "custom", key: "short_description" },
            { namespace: "custom", key: "perks" },
            { namespace: "custom", key: "list_of_benefits" },
            { namespace: "custom", key: "ingredients" },
            { namespace: "custom", key: "included_products" }
          ]) {
            namespace
            key
            value
            references(first: 20) {
              edges {
                node {
                  ... on Metaobject {
                    fields {
                      key
                      value
                      reference {
                        ... on MediaImage {
                          image {
                            url
                            altText
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }

          sellingPlanGroups(first: 1) {
            edges {
              node {
                sellingPlans(first: 1) {
                  edges {
                    node {
                      id
                      priceAdjustments {
                        adjustmentValue {
                          ... on SellingPlanPercentagePriceAdjustment {
                            adjustmentPercentage
                          }
                          ... on SellingPlanFixedAmountPriceAdjustment {
                            adjustmentAmount {
                              amount
                            }
                          }
                          ... on SellingPlanFixedPriceAdjustment {
                            price {
                              amount
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }

          images(first: 1) {
            edges {
              node {
                url
                altText
              }
            }
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          variants(first: 10) {
            edges {
              node {
                id
                title
                sku
                price {
                  amount
                  currencyCode
                }
                availableForSale
              }
            }
          }
        }
      }
    }
  }`;
}

async function getProducts(query) {
  const config = window.storefrontConfig;
  if (!config?.token || !config?.url) {
    throw new Error(
      "Storefront API not configured. Set the access token in theme settings.",
    );
  }

  const res = await fetch(config.url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": config.token,
    },
    body: JSON.stringify({ query }),
  });

  if (!res.ok) {
    throw new Error(`Storefront API error: ${res.status} ${res.statusText}`);
  }

  const json = await res.json();

  if (json.errors?.length) {
    console.warn(
      "[quiz-filtering] GraphQL warnings:",
      json.errors.map((e) => e.message).join(", "),
    );
    if (!json.data?.products) {
      throw new Error(
        `GraphQL errors: ${json.errors.map((e) => e.message).join(", ")}`,
      );
    }
  }

  return json.data.products;
}

function richTextToHtml(value) {
  if (!value) return "";
  let parsed;
  try {
    parsed = JSON.parse(value);
  } catch {
    return value;
  }
  function render(n) {
    if (!n) return "";
    if (n.type === "text") {
      let s = n.value || "";
      if (n.bold) s = `<strong>${s}</strong>`;
      if (n.italic) s = `<em>${s}</em>`;
      return s;
    }
    const children = (n.children || []).map(render).join("");
    switch (n.type) {
      case "root":
        return children;
      case "paragraph":
        return `<p>${children}</p>`;
      case "heading":
        return `<h${n.level}>${children}</h${n.level}>`;
      case "list":
        return n.listType === "ordered"
          ? `<ol>${children}</ol>`
          : `<ul>${children}</ul>`;
      case "list-item":
        return `<li>${children}</li>`;
      case "link":
        return `<a href="${n.url || ""}">${children}</a>`;
      default:
        return children;
    }
  }
  return render(parsed);
}

function flattenProductNode(node) {
  const firstImage = node.images.edges[0]?.node || null;
  const tagsMetafield = node.metafields?.find(
    (m) => m?.namespace === "custom" && m?.key === "tags",
  );
  const shortDescMetafield = node.metafields?.find(
    (m) => m?.namespace === "custom" && m?.key === "short_description",
  );
  const perksMetafield = node.metafields?.find(
    (m) => m?.namespace === "custom" && m?.key === "perks",
  );
  const benefitsMetafield = node.metafields?.find(
    (m) => m?.namespace === "custom" && m?.key === "list_of_benefits",
  );
  const ingredientsMetafield = node.metafields?.find(
    (m) => m?.namespace === "custom" && m?.key === "ingredients",
  );
  const includedProductsMetafield = node.metafields?.find(
    (m) => m?.namespace === "custom" && m?.key === "included_products",
  );
  const tags = node.tags;
  let displayTags = [];
  try {
    displayTags = tagsMetafield?.value ? JSON.parse(tagsMetafield.value) : [];
  } catch (e) {
    displayTags = [];
  }
  const perks = (perksMetafield?.references?.edges || [])
    .filter((e) => e.node != null)
    .map(({ node: obj }) => {
      const fields = obj.fields || [];
      return {
        text: fields.find((f) => f.key === "text")?.value || "",
        image: fields.find((f) => f.key === "image")?.reference?.image || null,
      };
    });
  const benefits = (benefitsMetafield?.references?.edges || [])
    .filter((e) => e.node != null)
    .map(({ node: obj }) => {
      const fields = obj.fields || [];
      return {
        title: fields.find((f) => f.key === "title")?.value || "",
        description: richTextToHtml(
          fields.find((f) => f.key === "description")?.value,
        ),
      };
    });
  const ingredients = richTextToHtml(ingredientsMetafield?.value);
  let includedProductIds = [];
  try {
    includedProductIds = includedProductsMetafield?.value
      ? JSON.parse(includedProductsMetafield.value)
      : [];
  } catch (e) {
    includedProductIds = [];
  }
  // Strip GID prefix: "gid://shopify/SellingPlan/12345" → "12345"
  const sellingPlanNode =
    node.sellingPlanGroups?.edges?.[0]?.node?.sellingPlans?.edges?.[0]?.node;
  const sellingPlanId = sellingPlanNode?.id?.split("/").pop() || null;

  // Compute subscription price from the selling plan's first price adjustment
  const basePrice = parseFloat(node.priceRange.minVariantPrice.amount);
  let subscriptionPrice = null;
  const adj = sellingPlanNode?.priceAdjustments?.[0]?.adjustmentValue;
  if (adj) {
    if (adj.adjustmentPercentage != null) {
      subscriptionPrice = basePrice * (1 - adj.adjustmentPercentage / 100);
    } else if (adj.adjustmentAmount?.amount != null) {
      subscriptionPrice = basePrice - parseFloat(adj.adjustmentAmount.amount);
    } else if (adj.price?.amount != null) {
      subscriptionPrice = parseFloat(adj.price.amount);
    }
  }

  const result = {
    id: node.id,
    title: node.title,
    handle: node.handle,
    sellingPlanId,
    tags,
    displayTags,
    perks,
    benefits,
    ingredients,
    includedProductIds,
    includedProducts: [],
    description: shortDescMetafield?.value || node.description || "",
    image: firstImage
      ? { url: firstImage.url, altText: firstImage.altText }
      : null,
    price: node.priceRange.minVariantPrice,
    subscriptionPrice,
    variants: node.variants.edges.map((e) => ({
      id: e.node.id,
      title: e.node.title,
      sku: e.node.sku,
      price: e.node.price,
      availableForSale: e.node.availableForSale,
    })),
  };
  return result;
}

function sortProductsByChunks(
  products,
  lovedProteins,
  petTags,
  priorityHandles,
) {
  const toTagArray = (sel) =>
    Array.isArray(sel.tag) ? sel.tag : sel.tag ? [sel.tag] : [];
  const lovedTags = (lovedProteins || [])
    .flatMap(toTagArray)
    .map((t) => t.toLowerCase());
  const prioritySet = new Set(
    (priorityHandles || []).map((h) => h.toLowerCase()),
  );
  const hasSensitive = (petTags || []).some((t) =>
    t.toLowerCase().includes("sensitive"),
  );

  // Within each chunk: rank by loved-protein tag matches, then by sensitive digestion match
  const byProteinScore = (arr) =>
    arr
      .map((p) => ({
        p,
        score: p.tags.filter((t) => lovedTags.includes(t.toLowerCase())).length,
        sensitive:
          hasSensitive &&
          p.tags.some((t) => t.toLowerCase().includes("sensitive"))
            ? 1
            : 0,
      }))
      .sort((a, b) => b.score - a.score || b.sensitive - a.sensitive)
      .map(({ p }) => p);

  const productByHandle = new Map(
    products.map((p) => [p.handle.toLowerCase(), p]),
  );
  const priorityChunk = (priorityHandles || [])
    .map((h) => productByHandle.get(h.toLowerCase()))
    .filter(Boolean);

  const remaining = products.filter(
    (p) => !prioritySet.has(p.handle.toLowerCase()),
  );

  const lovedChunk = [];
  const neutralChunk = [];

  remaining.forEach((product) => {
    const productTags = product.tags.map((t) => t.toLowerCase());
    if (
      lovedTags.length > 0 &&
      lovedTags.some((t) => productTags.includes(t))
    ) {
      lovedChunk.push(product);
    } else {
      neutralChunk.push(product);
    }
  });

  return [
    ...byProteinScore(priorityChunk),
    ...byProteinScore(lovedChunk),
    ...byProteinScore(neutralChunk),
  ];
}

async function resolveProductNodes(ids) {
  if (!ids?.length) return {};
  const config = window.storefrontConfig;
  if (!config?.token || !config?.url) return {};

  const idList = ids.map((id) => JSON.stringify(id)).join(", ");
  const query = `{
    nodes(ids: [${idList}]) {
      ... on Product {
        id
        title
        images(first: 1) {
          edges { node { url altText } }
        }
      }
    }
  }`;

  try {
    const res = await fetch(config.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": config.token,
      },
      body: JSON.stringify({ query }),
    });
    const json = await res.json();
    const map = {};
    (json.data?.nodes || []).forEach((node) => {
      if (node?.id) {
        map[node.id] = {
          id: node.id,
          title: node.title,
          image: node.images?.edges?.[0]?.node
            ? {
                url: node.images.edges[0].node.url,
                altText: node.images.edges[0].node.altText,
              }
            : null,
        };
      }
    });
    return map;
  } catch (e) {
    console.warn("[quiz-shopify] resolveProductNodes failed:", e);
    return {};
  }
}

async function fetchProductsForTags(
  lovedProteins,
  hatedProteins,
  petTags,
  priorityHandles,
) {
  const toTagArray = (sel) =>
    Array.isArray(sel.tag) ? sel.tag : sel.tag ? [sel.tag] : [];
  // Deduplicate after lowercasing — some selections carry both-case variants
  // in their tag array (e.g. ["salmon", "Salmon"]), which would emit redundant
  // -tag:"salmon" exclusions and inflate the query string unnecessarily.
  const hatedTags = [
    ...new Set(
      (hatedProteins || []).flatMap(toTagArray).map((t) => t.toLowerCase()),
    ),
  ];

  const allProducts = [];
  let cursor = null;
  let hasNextPage = true;

  while (hasNextPage) {
    const page = await getProducts(productsQuery(cursor, hatedTags));
    allProducts.push(...page.edges.map((e) => flattenProductNode(e.node)));
    hasNextPage = page.pageInfo.hasNextPage;
    cursor = page.pageInfo.endCursor;
  }

  // Resolve included product GIDs in a single nodes query
  const allIncludedIds = [
    ...new Set(allProducts.flatMap((p) => p.includedProductIds || [])),
  ];
  if (allIncludedIds.length) {
    const nodeMap = await resolveProductNodes(allIncludedIds);
    allProducts.forEach((p) => {
      p.includedProducts = (p.includedProductIds || [])
        .map((id) => nodeMap[id])
        .filter(Boolean);
    });
  }

  console.log(
    "[monthly-filter] fetchProductsForTags — products returned (" +
      allProducts.length +
      "):\n" +
      allProducts
        .map(
          (p, i) =>
            `  ${i + 1}. ${p.title} | tags: ${(p.tags || []).join(", ")}`,
        )
        .join("\n"),
  );
  return sortProductsByChunks(
    allProducts,
    lovedProteins,
    petTags,
    priorityHandles,
  );
}

// ─── Pouch calculator ────────────────────────────────────────────────────────

// Maps answer index (0-based) on the usage/quantity frame step to monthly pouch count
// index 0 = topper (36 = 3 boxes), 1 = daily (60 = 5 boxes), 2 = usage-more (84 = 7 boxes)
const USAGE_POUCHES = [36, 60, 84];

function findUsageStepNumber() {
  const steps = window.quizSteps || {};
  const pattern = /\b(usage|use|quantity)\b/i;
  for (const [num, step] of Object.entries(steps)) {
    if ((step.display || "").toLowerCase() !== "frame") continue;
    if (pattern.test(step.title || "") || pattern.test(step.handle || "")) {
      return parseInt(num, 10);
    }
  }
  return null;
}

function calcPouches(statePet) {
  const stepNum = findUsageStepNumber();
  if (stepNum == null) return null;
  const ans = statePet.answers?.[stepNum];
  if (ans?.answerIndex == null) return null;
  return USAGE_POUCHES[ans.answerIndex] ?? null;
}

// Exposed for the results page URL fallback (re-fetches products cross-device)
window.fetchOptimealProductsForPet = (loved, hated, petTags) =>
  fetchProductsForTags(
    loved,
    hated,
    petTags || [],
    window.quizPriorityProductHandles || [],
  );

/**
 * Fetch full product data (including perks metafields) for specific numeric product IDs.
 * Used to enrich trial-box products which are only known by their Shopify numeric ID.
 * @param {string[]} numericIds
 * @param {string[]} hatedTags - lowercase tag strings to exclude (same format as productsQuery exclusions)
 */
async function fetchProductsByIds(numericIds, hatedTags = []) {
  const ids = (numericIds || []).filter(Boolean);
  if (!ids.length) return [];

  const config = window.storefrontConfig;
  if (!config?.token || !config?.url) return [];

  const gids = ids.map((id) => `gid://shopify/Product/${id}`);
  const idsArg = gids.map((g) => JSON.stringify(g)).join(", ");

  const query = `{
    nodes(ids: [${idsArg}]) {
      ... on Product {
        id title handle tags description
        metafields(identifiers: [
          { namespace: "custom", key: "tags" },
          { namespace: "custom", key: "short_description" },
          { namespace: "custom", key: "perks" },
          { namespace: "custom", key: "list_of_benefits" },
          { namespace: "custom", key: "ingredients" },
          { namespace: "custom", key: "included_products" }
        ]) {
          namespace key value
          references(first: 20) {
            edges {
              node {
                ... on Metaobject {
                  fields {
                    key value
                    reference {
                      ... on MediaImage { image { url altText } }
                    }
                  }
                }
              }
            }
          }
        }
        sellingPlanGroups(first: 1) {
          edges { node { sellingPlans(first: 1) { edges { node {
            id
            priceAdjustments { adjustmentValue {
              ... on SellingPlanPercentagePriceAdjustment { adjustmentPercentage }
              ... on SellingPlanFixedAmountPriceAdjustment { adjustmentAmount { amount } }
              ... on SellingPlanFixedPriceAdjustment { price { amount } }
            }}
          }}}}}
        }
        images(first: 1) { edges { node { url altText } } }
        priceRange { minVariantPrice { amount currencyCode } }
        variants(first: 10) {
          edges { node { id title sku price { amount currencyCode } availableForSale } }
        }
      }
    }
  }`;

  try {
    const res = await fetch(config.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": config.token,
      },
      body: JSON.stringify({ query }),
    });
    if (!res.ok) return [];
    const json = await res.json();
    const excluded = new Set((hatedTags || []).map((t) => t.toLowerCase()));
    const allNodes = (json.data?.nodes || [])
      .filter(Boolean)
      .map(flattenProductNode);

    // Resolve included product GIDs (same as fetchProductsForTags)
    const allIncludedIds = [
      ...new Set(allNodes.flatMap((p) => p.includedProductIds || [])),
    ];
    if (allIncludedIds.length) {
      const nodeMap = await resolveProductNodes(allIncludedIds);
      allNodes.forEach((p) => {
        p.includedProducts = (p.includedProductIds || [])
          .map((id) => nodeMap[id])
          .filter(Boolean);
      });
    }

    return allNodes.filter(
      (p) =>
        excluded.size === 0 ||
        !(p.tags || []).some((t) => excluded.has(t.toLowerCase())),
    );
  } catch (e) {
    console.warn("[quiz-filtering] fetchProductsByIds failed:", e);
    return [];
  }
}

window.fetchOptimealProductsByIds = fetchProductsByIds;

document.addEventListener("quiz:complete", async () => {
  const state = window.OptimealQuizState;

  const stripHtml = (str) => {
    const el = document.createElement("div");
    el.innerHTML = str || "";
    return (el.textContent || el.innerText || "").trim();
  };

  const rawPets = (state?.pets || [])
    .filter((p) => p.unsupportedAtStep == null)
    .map((p) => {
      const activityText = stripHtml(p.formAnswers?.activity?.text || "");

      // Collect all inline answer texts to detect age/life-stage in buildSubheading
      const answerTexts = Object.values(p.answers || {}).map((a) =>
        stripHtml(a.text || ""),
      );

      return {
        name: p.name,
        tags: p.tags || [],
        activity: activityText,
        answerTexts,
        answersByHandle: p.answersByHandle || {},
        pouches: calcPouches(p),
      };
    });

  window.OptimealResults = window.OptimealResults || {};
  window.OptimealResults.loading = true;
  window.OptimealResults.error = null;
  window.OptimealResults.pets = [];

  try {
    const petsWithProducts = await Promise.all(
      rawPets.map(async (pet) => {
        const loved = pet.answersByHandle["love-proteins"]?.selections ?? [];
        // Monthly plan: each pet only filters its own hated proteins
        const hated = pet.answersByHandle["hate-proteins"]?.selections ?? [];
        const products = await fetchProductsForTags(
          loved,
          hated,
          pet.tags,
          window.quizPriorityProductHandles || [],
        );
        return {
          name: pet.name,
          tags: pet.tags,
          activity: pet.activity,
          answerTexts: pet.answerTexts,
          answersByHandle: pet.answersByHandle,
          pouches: pet.pouches,
          products,
        };
      }),
    );

    window.OptimealResults.pets = petsWithProducts;
    window.OptimealResults.loading = false;

    // Persist per-pet results for the results page; clear stale extras from any prior session
    try {
      localStorage.setItem(
        "optimeal_quiz_results",
        JSON.stringify({
          pets: petsWithProducts,
        }),
      );
      localStorage.removeItem("optimeal_quiz_extras");
    } catch (e) {
      console.warn("[quiz-filtering] Could not save to localStorage:", e);
    }

    const totalCount = petsWithProducts.reduce(
      (sum, p) => sum + p.products.length,
      0,
    );
    document.dispatchEvent(
      new CustomEvent("quiz:filtered", {
        detail: { count: totalCount, pets: petsWithProducts },
      }),
    );
  } catch (err) {
    console.error("[quiz-filtering] Error:", err);
    window.OptimealResults.error = err.message;
    window.OptimealResults.loading = false;

    document.dispatchEvent(
      new CustomEvent("quiz:filtered", {
        detail: { count: 0, pets: [], error: err.message },
      }),
    );
  }
});
